import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { API, useAuth } from "../App";
import { Section, GlassCard, Eyebrow } from "../components/Section";
import NeuralNetworkBackground, { ScaleWatermark } from "../components/NeuralNetworkBackground";
import {
  CreditCard,
  Check,
  Sparkles,
  ShieldCheck,
  Loader2,
  ArrowRight,
  AlertCircle,
} from "lucide-react";

const detectBrand = (num) => {
  const n = num.replace(/\s+/g, "");
  if (/^4/.test(n)) return "visa";
  if (/^5[1-5]/.test(n)) return "mastercard";
  if (/^3[47]/.test(n)) return "amex";
  if (/^6(?:011|5)/.test(n)) return "discover";
  if (/^(506[0-9]|650[0-9]|507[8-9])/.test(n)) return "verve";
  return "card";
};

const formatCardNumber = (v) => v.replace(/\D/g, "").slice(0, 19).replace(/(.{4})/g, "$1 ").trim();

const PERKS = [
  "Verified lawyer profile + Bar credentials display",
  "Matched client requests routed to your inbox",
  "AI Copilot — research, drafting, case strategy",
  "Escrow-protected milestone payments",
  "Calendar + court-date deadline alerts",
  "Justice Wallet donor-funded case opportunities",
  "Ratings, performance and visibility engine",
];

const LawyerSubscribe = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [currency, setCurrency] = useState("ngn");
  const [card, setCard] = useState({ number: "", name: "", expMonth: "", expYear: "", cvc: "" });
  const [submitting, setSubmitting] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loadingSub, setLoadingSub] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/lawyers/subscribe/status`, { withCredentials: true });
        setSubscription(data.subscription);
      } catch (err) {
        // ignore
      } finally {
        setLoadingSub(false);
      }
    })();
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" state={{ from: { pathname: "/lawyer-subscribe" } }} replace />;

  const brand = detectBrand(card.number);
  const planLabel = currency === "ngn" ? "₦10,000 / month" : "$10 / month";

  const handleSubmit = async (e) => {
    e.preventDefault();
    const digits = card.number.replace(/\s+/g, "");
    if (digits.length < 12 || digits.length > 19) {
      toast.error("Please enter a valid card number");
      return;
    }
    if (!card.name.trim()) { toast.error("Cardholder name is required"); return; }
    const m = parseInt(card.expMonth, 10), y = parseInt(card.expYear, 10);
    if (!m || m < 1 || m > 12) { toast.error("Invalid expiry month"); return; }
    if (!y || y < new Date().getFullYear()) { toast.error("Card has expired"); return; }

    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/lawyers/subscribe`,
        {
          card_number: digits,
          card_brand: brand,
          exp_month: m,
          exp_year: y,
          cardholder_name: card.name.trim(),
          currency,
        },
        { withCredentials: true }
      );
      setSubscription(data.subscription);
      toast.success("You're on board! No charge today — pilot subscription active.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save subscription");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Remove your saved card and exit pilot subscription?")) return;
    try {
      await axios.delete(`${API}/lawyers/subscribe`, { withCredentials: true });
      setSubscription(null);
      toast.success("Subscription removed");
    } catch (err) {
      toast.error("Failed to cancel");
    }
  };

  return (
    <main className="bg-ink text-white" data-testid="lawyer-subscribe-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-50"><NeuralNetworkBackground density={22} /></div>
        <ScaleWatermark />
        <div className="turquoise-aura -top-32 -left-32 opacity-60" />
        <div className="grain-overlay" />
        <div className="container relative z-10 mx-auto px-6 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-4xl">
            <div className="aj-chip aj-chip-gold mb-6"><Sparkles className="w-3 h-3" /> Pilot Phase · No Charge Today</div>
            <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl text-white leading-[1.05]">
              Get on board at{" "}
              <span className="italic text-gold-gradient">no charge today.</span>
            </h1>
            <p className="font-inter text-base md:text-lg text-white/65 mt-6 max-w-2xl leading-relaxed">
              Save a card on file to join the AccessJustice.Global Pilot. We will not bill you
              during the pilot — your card is validated and stored for future activation only.
              We&apos;ll email 30 days before any change.
            </p>
          </motion.div>
        </div>
      </section>

      <Section className="!pt-0">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Pricing card */}
          <div className="lg:col-span-5">
            <GlassCard glow className="!p-8 h-full">
              <Eyebrow className="mb-4">Pro Subscription</Eyebrow>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-playfair italic text-6xl text-aj-gradient leading-none">{currency === "ngn" ? "₦10,000" : "$10"}</span>
                <span className="font-montserrat text-xs uppercase tracking-[0.2em] text-white/55">/ month</span>
              </div>
              <p className="font-inter text-xs text-white/45 mb-6">{currency === "ngn" ? "Nigerian Naira" : "US Dollars"}</p>

              {/* Currency toggle */}
              <div className="flex gap-2 mb-6" data-testid="currency-toggle">
                {[
                  { v: "ngn", l: "NGN ₦" },
                  { v: "usd", l: "USD $" },
                ].map((c) => (
                  <button
                    key={c.v}
                    type="button"
                    onClick={() => setCurrency(c.v)}
                    data-testid={`currency-${c.v}`}
                    className={`flex-1 px-4 py-2.5 rounded-full font-montserrat text-xs uppercase tracking-wider font-semibold transition-all ${
                      currency === c.v
                        ? "bg-turquoise text-ink"
                        : "bg-white/[0.03] border border-white/10 text-white/70 hover:border-turquoise/40"
                    }`}
                  >
                    {c.l}
                  </button>
                ))}
              </div>

              {/* Pilot banner */}
              <div className="rounded-xl border border-gold-400/40 bg-gold-400/[0.06] p-4 mb-6 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-gold-300 mt-0.5 shrink-0" />
                <div>
                  <div className="font-montserrat text-[10px] uppercase tracking-[0.22em] text-gold-200 mb-1">Pilot Phase · No Charges</div>
                  <p className="font-inter text-xs text-white/75 leading-relaxed">
                    Card validated but not billed. Free until further notice. We&apos;ll email 30 days before any change.
                  </p>
                </div>
              </div>

              <ul className="space-y-2.5">
                {PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-2.5">
                    <div className="w-4 h-4 rounded-full bg-turquoise/15 border border-turquoise/40 flex items-center justify-center mt-0.5 shrink-0">
                      <Check className="w-2.5 h-2.5 text-turquoise" />
                    </div>
                    <span className="font-inter text-sm text-white/80">{p}</span>
                  </li>
                ))}
              </ul>
            </GlassCard>
          </div>

          {/* Card form / Current subscription */}
          <div className="lg:col-span-7">
            {loadingSub ? (
              <div className="flex items-center gap-2 text-white/55"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : subscription ? (
              <GlassCard glow className="!p-8" data-testid="subscription-active">
                <div className="aj-chip aj-chip-gold mb-4">Pilot Active · No Charge</div>
                <h2 className="font-playfair text-3xl text-white mb-6">You&apos;re on board.</h2>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <div className="rounded-xl border border-white/10 bg-white/[0.025] p-4">
                    <div className="font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">Plan</div>
                    <div className="font-playfair text-xl text-white">{subscription.plan_label}</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.025] p-4">
                    <div className="font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">Status</div>
                    <div className="font-playfair text-xl text-gold-gradient capitalize">{subscription.billing_status.replace(/_/g, " ")}</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.025] p-4">
                    <div className="font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">Card</div>
                    <div className="font-playfair text-xl text-white capitalize">{subscription.card_brand} ···· {subscription.card_last4}</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.025] p-4">
                    <div className="font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">Expires</div>
                    <div className="font-playfair text-xl text-white">{String(subscription.card_exp_month).padStart(2, "0")} / {String(subscription.card_exp_year).slice(-2)}</div>
                  </div>
                </div>
                <div className="rounded-xl border border-turquoise/30 bg-turquoise/[0.05] p-4 mb-6 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-turquoise mt-0.5 shrink-0" />
                  <p className="font-inter text-xs text-white/75 leading-relaxed">
                    {subscription.pilot_notice}
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => navigate("/dashboard")} data-testid="sub-go-dashboard" className="btn-aj-primary">
                    Go to Lawyer Dashboard <ArrowRight className="w-4 h-4" />
                  </button>
                  <button onClick={handleCancel} data-testid="sub-cancel" className="btn-aj-ghost">
                    Remove subscription
                  </button>
                </div>
              </GlassCard>
            ) : (
              <GlassCard className="!p-8">
                <Eyebrow className="mb-2">Payment Details</Eyebrow>
                <h2 className="font-playfair text-2xl text-white mb-1">Save your card to activate the pilot</h2>
                <p className="font-inter text-xs text-white/55 mb-6">No charges will be made today. Card validated and stored for future activation only.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Cardholder Name</label>
                    <input
                      type="text"
                      value={card.name}
                      onChange={(e) => setCard({ ...card, name: e.target.value })}
                      placeholder="As shown on card"
                      required
                      data-testid="card-name"
                      className="w-full bg-white/[0.04] border border-white/10 rounded-full px-5 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Card Number</label>
                    <div className="relative">
                      <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                      <input
                        type="text"
                        value={card.number}
                        onChange={(e) => setCard({ ...card, number: formatCardNumber(e.target.value) })}
                        placeholder="1234 5678 9012 3456"
                        required
                        autoComplete="cc-number"
                        data-testid="card-number"
                        className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-20 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 transition-all tracking-wider"
                      />
                      {brand !== "card" && (
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 font-montserrat text-[10px] uppercase tracking-wider text-turquoise capitalize">{brand}</span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">MM</label>
                      <input
                        type="text"
                        value={card.expMonth}
                        onChange={(e) => setCard({ ...card, expMonth: e.target.value.replace(/\D/g, "").slice(0, 2) })}
                        placeholder="12"
                        required
                        data-testid="card-exp-month"
                        className="w-full bg-white/[0.04] border border-white/10 rounded-full px-4 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 transition-all text-center"
                      />
                    </div>
                    <div>
                      <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">YYYY</label>
                      <input
                        type="text"
                        value={card.expYear}
                        onChange={(e) => setCard({ ...card, expYear: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                        placeholder="2028"
                        required
                        data-testid="card-exp-year"
                        className="w-full bg-white/[0.04] border border-white/10 rounded-full px-4 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 transition-all text-center"
                      />
                    </div>
                    <div>
                      <label className="block font-montserrat text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">CVC</label>
                      <input
                        type="text"
                        value={card.cvc}
                        onChange={(e) => setCard({ ...card, cvc: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                        placeholder="123"
                        required
                        data-testid="card-cvc"
                        className="w-full bg-white/[0.04] border border-white/10 rounded-full px-4 py-3 font-inter text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-turquoise/60 transition-all text-center"
                      />
                    </div>
                  </div>

                  <div className="text-[11px] font-inter text-white/40 leading-relaxed">
                    By confirming, you agree to our{" "}
                    <a href="https://customer-assets.emergentagent.com/job_ai-legal-test/artifacts/4ehfyx0m_TERMS%20OF%20SERVICE%20-JUSTICE-AI.pdf" target="_blank" rel="noopener noreferrer" className="text-turquoise hover:underline">Terms of Service</a>.
                    During the pilot, no charges will be made. Your card details are stored securely for future activation only.
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="subscribe-submit"
                    className="btn-aj-primary w-full !py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting
                      ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>)
                      : (<>Activate Pilot · {planLabel} · No Charge <ArrowRight className="w-4 h-4" /></>)}
                  </button>
                </form>
              </GlassCard>
            )}
          </div>
        </div>

        <div className="text-center mt-12">
          <Link to="/for-lawyers" className="btn-aj-ghost">← Back to For Lawyers</Link>
        </div>
      </Section>
    </main>
  );
};

export default LawyerSubscribe;
